import React from 'react';
import { getPeriod } from 'src/utils/periodTime';
import { TopUpProcedure } from 'src/components/TopUpProcedure/TopUpProcedure';
import { WithdrawProcedure } from 'src/components/WithdrawProcedure/WithdrawProcedure';
import { ManualAdjustmentProcedure } from 'src/components/ManualAdjustmentProcedure/ManualAdjustmentProcedure';
import { FreeBetsProcedure } from 'src/components//FreeBetsProcedure/FreeBetsProcedure';
import { Separator, AuditAllButton } from './BankingTab.style';
import Icon from 'src/components/Icon/Icon';
import { observer } from 'src/utils/mobx-react';
import classNames from 'classnames';
import { AutomaticWithdrawalHistory } from 'src/components/CustomersBar/CustomerTab/BankingTab/AutimaticWithdrawalHistory/AutomaticWithdrawalHistory';
import DateRangeForm from 'src/components/DateRangeForm/DateRangeForm.jsx';
import { useAppStateContext } from 'src/AppState/AppState';
import { DepositHistory } from 'src/components/CustomersBar/CustomerTab/BankingTab/DepositHistory/DepositHistory';
import { ManualAdjustments } from 'src/components/CustomersBar/CustomerTab/BankingTab/ManualAdjustments/ManualAdjustments';
import { PendingWithdrawals } from 'src/components/CustomersBar/CustomerTab/BankingTab/PendingWithdrawals/PendingWithdrawals';
import { BankingTabItems } from 'src/components/CustomersBar/CustomerTab/BankingTab/BankingTabItems';
import { List } from 'immutable';
import { FreeBetsHistory } from 'src/components/CustomersBar/CustomerTab/BankingTab/FreeBetsHistory/FreeBetsHistory';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { FreeBetHistoryBonusEngine } from 'src/components/CustomersBar/CustomerTab/BankingTab/FreeBetsHistory/FreeBetsHistoryBonusEngine';
import { WithdrawalHistory } from 'src/components/CustomersBar/CustomerTab/BankingTab/WithdrawalHistory/WithdrawalHistory';

type BankingTabPropsType = {
    id: number;
    permissionGrants: List<unknown>;
    onQueryAudits: (
        id: string,
        topic: string,
        tag: string | Array<string>,
        filterName: string | null
    ) => (e: React.MouseEvent<Element, MouseEvent>) => void;
    showAllAudits: (id: string, topic: string, tag: string[]) => (e: React.MouseEvent<Element, MouseEvent>) => void;
};

type MethodLabelsType =
    | 'credit-card'
    | 'bank-transfer'
    | 'cash'
    | 'cheque'
    | 'manual'
    | 'deposit-card'
    | 'casino-bonus';

const METHOD_LABELS = {
    'credit-card': 'Debit card',
    'bank-transfer': 'Bank transfer',
    cash: 'Cash',
    cheque: 'Cheque',
    manual: 'Manual',
    'deposit-card': 'Deposit card',
    'casino-bonus': 'Casino bonus',
    'open-banking': 'Open banking',
    'casiono-bonus': 'Casino bonus',
};

function isStringInMethodsLabels(value: string | null | undefined): value is MethodLabelsType {
    return Object.keys(METHOD_LABELS).includes(value ?? '');
}

export const pickMethodLabel = (adjustmentType: string | null | undefined): string => {
    return METHOD_LABELS[isStringInMethodsLabels(adjustmentType) ? adjustmentType : 'deposit-card'];
};

type TypeLabelsType = 'withdrawal' | 'deposit';

const TYPE_LABELS = {
    withdrawal: 'Withdrawal',
    deposit: 'Deposit',
};

function isStringInTypeLabels(value: string | null | undefined): value is TypeLabelsType {
    return Object.keys(TYPE_LABELS).includes(value ?? '');
}

export const pickTypeLabel = (adjustmentType: string | null | undefined): string => {
    return isStringInTypeLabels(adjustmentType) ? TYPE_LABELS[adjustmentType] : '';
};

export const BankingTab = observer('BankingTab', (props: BankingTabPropsType) => {
    const { routerBackoffice, bankingTabState } = useAppStateContext();
    const customerId = props.id;
    const common = useCommon();
    const configState = ConfigState.get(common);
    const { usePaymentFormForUniverse, showBonusEngine: isBonusEngineEnabled } = configState.config;
    const state = bankingTabState.get(customerId);

    const time = getPeriod(state.period.startDate, state.period.endDate);

    const queryAudits = (): void => {
        void state.queryAudits(() => {
            props.onQueryAudits(
                `wallet_updates_account-${customerId}`,
                'wallet_updates',
                [`account-${customerId}`],
                null
            );
        });
    };

    const customerParams = routerBackoffice.getParametersString('customers', 'tab.tab.tab') ?? 'deposit';

    return (
        <div className='banking-tab'>
            <div className='row'>
                <div className='col-6'>
                    <BankingTabItems />
                    {customerParams === 'deposit' && (
                        <TopUpProcedure
                            customerId={customerId}
                            permissionGrants={props.permissionGrants}
                            usePaymentFormForUniverse={usePaymentFormForUniverse}
                            onQueryAudits={queryAudits}
                        />
                    )}

                    {customerParams === 'withdrawal' && (
                        <WithdrawProcedure
                            customerId={customerId}
                            onQueryAudits={queryAudits}
                            permissionGrants={props.permissionGrants}
                            usePaymentFormForUniverse={usePaymentFormForUniverse}
                        />
                    )}

                    {customerParams === 'manual-adjustment' && (
                        <ManualAdjustmentProcedure
                            customerId={customerId}
                            onQueryAudits={queryAudits}
                        />
                    )}

                    {customerParams === 'freebet-procedure' && (
                        <FreeBetsProcedure
                            key='freebet-procedure'
                            label='Free Bets'
                            permissionGrants={props.permissionGrants}
                            customerId={customerId}
                            refreshFreebetLists={state.refreshFreebetLists}
                            currency={state.currency}
                        />
                    )}
                    <Separator />
                    <AuditAllButton
                        onClick={props.showAllAudits(`wallet_updates_account-${customerId}`, 'wallet_updates', [
                            `account-${customerId}`,
                        ])}
                        href='#'
                    >
                        Audit all
                    </AuditAllButton>
                </div>
                <div className='col-6'>
                    <AutomaticWithdrawalHistory customerId={customerId} />
                    <h3>
                        Deposit history&nbsp;&nbsp;
                        {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                        <select
                            value={time}
                            onChange={state.changeTime}
                        >
                            <option value='today'>Today</option>
                            <option value='3-days'>Last 3 days</option>
                            <option value='7-days'>Last 7 days</option>
                            <option value='week'>This week</option>
                            <option value='month'>This month</option>
                        </select>
                        <button
                            className='btn calendar-btn'
                            onClick={state.onCalendarClick}
                        >
                            <Icon name='calendar' />
                        </button>
                        <div className={classNames('calendar-popup', { hidden: !state.showCalendar })}>
                            <DateRangeForm
                                label='Calendar'
                                onSubmit={state.onCalendarSubmit}
                                data={state.period}
                                onGenerateCSV={state.onGenerateCSV}
                            />
                        </div>
                    </h3>

                    <DepositHistory
                        depositTransactions={state.depositTransactions}
                        currency={state.currency}
                    />

                    <h3>Pending withdrawals</h3>

                    <PendingWithdrawals
                        withdrawalsList={state.withdrawalsList}
                        cancelWithdrawal={state.cancelWithdrawal}
                        rejectWithdrawal={state.rejectWithdrawal}
                    />

                    <h3>Withdrawal History</h3>
                    <WithdrawalHistory withdrawalsList={state.latestWithdrawals} />

                    <h3>Manual adjustments</h3>

                    <ManualAdjustments manualAdjustmentsList={state.manualAdjustmentsList} />

                    <h3>Free Bets History</h3>

                    {isBonusEngineEnabled ? (
                        <FreeBetHistoryBonusEngine
                            customerId={customerId}
                            currency={state.currency}
                        />
                    ) : (
                        <FreeBetsHistory
                            customerId={customerId}
                            currency={state.currency}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});
